import { RefObject, useEffect, useState } from 'react';

interface Args extends IntersectionObserverInit {
	shouldFreezeOnceVisible?: boolean;
}

const useIntersectionObserver = (
	elementRef: RefObject<HTMLElement | null>,
	{ threshold = 0, root = null, rootMargin = '0%', shouldFreezeOnceVisible = false }: Args
): IntersectionObserverEntry | undefined => {
	const [entry, setEntry] = useState<IntersectionObserverEntry>();

	const frozen = entry?.isIntersecting && shouldFreezeOnceVisible;

	const updateEntry = ([e]: IntersectionObserverEntry[]): void => {
		setEntry(e);
	};

	useEffect(() => {
		const node = elementRef?.current; // DOM Ref
		const hasIOSupport = !!window.IntersectionObserver;

		if (!hasIOSupport || frozen || !node) return;

		const observerParams = { threshold, root, rootMargin };
		const observer = new IntersectionObserver(updateEntry, observerParams);

		observer.observe(node);

		return () => observer.disconnect();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [elementRef?.current, threshold, root, rootMargin, frozen]);

	return entry;
};

export const useOnIntersection = (ref: RefObject<HTMLElement | null>, onIntersection: () => void, options: Args) => {
	const entry = useIntersectionObserver(ref, options || {});
	const isVisible = !!entry?.isIntersecting;

	useEffect(() => {
		if (isVisible) {
			onIntersection();
		}
	}, [isVisible, onIntersection]);

	return [isVisible];
};

export default useIntersectionObserver;
